import React, { useContext, useMemo, useRef } from 'react';
import { Info, Pencil } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { EmptyEmailPreview } from './EmptyEmailPreview';
import { EmailPreview } from './EmailPreview';
import { AutomationEditorContext } from '../../AutomationEditorContext';
import { MergeFieldDropdown } from '../MergeFieldDropdown';
import { partnerApi } from '@/lib/api';
import { useAllFlags } from '@/hooks/useAllFlags';
import type { SenderProfile } from '@reach/shared-types/schema-mappings';
import type { SenderDetailsUpdate } from '../types';
import styles from '../SendMessageStep.module.css';

const SENDER_TOOLTIP =
  'Controls whose name and email recipients see in their inbox. ' +
  'Business Default uses your configured business sender. ' +
  "A contact profile personalizes each send to use that contact's assigned team member.";

function getSenderPreview(profileId: string | null | undefined, profiles: SenderProfile[]): string {
  const profile = profiles.find(p => p.profileId === profileId);
  if (profile) return `Contact's ${profile.label} <contact@send.email.com>`;
  return 'Your Business <marketing@send.yourdomain.com>';
}

interface EmailSetupProps {
  subject: string;
  previewText: string;
  html: string;
  css: string;
  onSubjectChange: (value: string) => void;
  onPreviewTextChange: (value: string) => void;
  senderProfileId: string | null | undefined;
  usePointOfContactSender?: boolean;
  onSenderProfileChange: (update: SenderDetailsUpdate) => void;
}

export const EmailSetup: React.FC<EmailSetupProps> = ({
  subject,
  previewText,
  html,
  css,
  onSubjectChange,
  onPreviewTextChange,
  senderProfileId,
  onSenderProfileChange,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { ld, partnerFlags } = useAllFlags();
  const senderProfilesEnabled = ld['enable-sender-profiles'];

  const subjectRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLInputElement>(null);

  const { data: schemaMappings } = useQuery({
    queryKey: ['partner-schema-mappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!senderProfilesEnabled,
  });

  const profiles: SenderProfile[] = senderProfilesEnabled
    ? (schemaMappings?.contactsSchema?.primarySource?.senderProfiles ?? [])
    : [];

  const showSenderPicker = !!partnerFlags?.engage?.enablePointOfContactSender;

  const handleSenderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onSenderProfileChange({ senderProfileId: null, usePointOfContactSender: false });
    } else {
      onSenderProfileChange({ senderProfileId: value, usePointOfContactSender: true });
    }
  };

  const senderPreview = getSenderPreview(senderProfileId, profiles);

  const insertMergeFieldAt = ({
    token,
    ref,
    value,
    onChange,
  }: {
    token: string;
    ref: React.RefObject<HTMLInputElement>;
    value: string;
    onChange: (v: string) => void;
  }) => {
    const input = ref.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const previewHtml = useMemo(() => {
    if (!html) return '';
    const styleTag = `<style>html, body { margin: 0; padding: 0; }${css || ''}</style>`;
    return html.replace('</head>', `${styleTag}</head>`);
  }, [html, css]);

  const hasContent = html && html.trim().length > 0;

  return (
    <div className={styles.emailPreviewPane}>
      <div className={styles.emailClientHeader}>
        {showSenderPicker && (
          <div className={`${styles.emailClientRow} ${styles.fromEmailRow}`}>
            <span className={styles.emailClientLabel}>From</span>
            <div className={styles.fromContent}>
              <div className={styles.fromControls}>
                {profiles.length === 0 ? (
                  <span className={styles.fromStaticValue}>Business Default</span>
                ) : (
                  <select
                    className={styles.fromSelect}
                    value={senderProfileId ?? ''}
                    onChange={handleSenderChange}>
                    <option value="">Business Default</option>
                    {profiles.map(p => (
                      <option key={p.profileId} value={p.profileId}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                )}
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className={styles.fromInfoBtn}
                        aria-label="About the From field">
                        <Info />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        sideOffset={6}
                        align="end"
                        className="z-50 max-w-xs px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg leading-relaxed">
                        {SENDER_TOOLTIP}
                        <Tooltip.Arrow className="fill-gray-900" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>
              <p className={styles.fromPreview}>{senderPreview}</p>
            </div>
          </div>
        )}
        <div className={styles.emailClientRow}>
          <span className={styles.emailClientLabel}>Subject</span>
          <div className={styles.emailClientField}>
            <input
              ref={subjectRef}
              type="text"
              value={subject}
              onChange={e => onSubjectChange(e.target.value)}
              placeholder="Enter subject line..."
              className={styles.emailClientInput}
            />
            <MergeFieldDropdown
              compact
              dropdownAlign="right"
              className={styles.emailHeaderMergeAction}
              onInsert={token =>
                insertMergeFieldAt({ token, ref: subjectRef, value: subject, onChange: onSubjectChange })
              }
            />
            <button
              type="button"
              className={styles.emailHeaderEditHint}
              onClick={() => subjectRef.current?.focus()}>
              <Pencil />
            </button>
          </div>
        </div>
        <div className={styles.emailClientRow}>
          <span className={styles.emailClientLabel}>Preview</span>
          <div className={styles.emailClientField}>
            <input
              ref={previewRef}
              type="text"
              value={previewText}
              onChange={e => onPreviewTextChange(e.target.value)}
              placeholder="Short preview shown in inbox..."
              className={styles.emailClientInput}
            />
            <MergeFieldDropdown
              compact
              dropdownAlign="right"
              className={styles.emailHeaderMergeAction}
              onInsert={token =>
                insertMergeFieldAt({
                  token,
                  ref: previewRef,
                  value: previewText,
                  onChange: onPreviewTextChange,
                })
              }
            />
            <button
              type="button"
              className={styles.emailHeaderEditHint}
              onClick={() => previewRef.current?.focus()}>
              <Pencil />
            </button>
          </div>
        </div>
      </div>
      <div className={styles.emailScrollArea}>
        <div className={styles.editBtnFloat}>
          <button type="button" className={styles.editButton} onClick={editor.openStripo}>
            <Pencil />
            Edit layout
          </button>
        </div>
        {hasContent ? <EmailPreview html={previewHtml} /> : <EmptyEmailPreview />}
      </div>
    </div>
  );
};
